import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChefHat, Salad, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";

/**
 * Splash / product selector.
 * Two elegant cards — Personal Chef (rose brand) and MarFit Meals (green/terracotta brand).
 * Each links to its own landing.
 */
const Splash = () => {
    useEffect(() => {
        document.title = "Synara Alvarenga — Cozinha para si";
    }, []);

    return (
        <div
            className="min-h-screen bg-[#f7f1eb] text-stone-900 relative overflow-hidden"
            data-testid="splash-page"
        >
            {/* subtle noise / grain overlay */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.03]"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                }}
            />

            <div className="relative max-w-6xl mx-auto px-6 py-14 md:py-24 flex flex-col min-h-screen">
                {/* Brand header */}
                <header className="text-center mb-14 md:mb-20">
                    <motion.img
                        src={BRAND.logo}
                        alt="Synara Alvarenga"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="mx-auto h-20 sm:h-24 md:h-28 w-auto object-contain mb-5"
                        data-testid="splash-logo"
                    />
                    <motion.p
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-[11px] md:text-xs tracking-[0.32em] uppercase text-stone-500"
                    >
                        Synara Alvarenga
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight mt-3 text-stone-900"
                    >
                        Como <em className="italic font-normal text-rose-800">te posso cozinhar</em>?
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        className="mt-5 text-stone-600 max-w-xl mx-auto text-base sm:text-lg leading-relaxed"
                    >
                        Duas formas de te dar mais tempo e mais sabor no dia-a-dia.
                        Escolhe a que faz mais sentido para ti.
                    </motion.p>
                </header>

                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 flex-1 items-stretch">
                    <ProductCard
                        to="/personal-chef"
                        variant="chef"
                        Icon={ChefHat}
                        eyebrow="Serviço"
                        image={BRAND.chefPhoto}
                        title={<>Personal <em className="italic font-normal">Chef</em></>}
                        tagline="Refeições privadas, jantares, eventos"
                        description="Cozinho na tua casa. Um menu à tua medida, mercado escolhido a dedo e uma experiência completa da cozinha à mesa."
                        cta="Descobrir o serviço"
                        testid="splash-card-chef"
                    />
                    <ProductCard
                        to="/marmitas"
                        variant="marfit"
                        Icon={Salad}
                        eyebrow="Novo"
                        image="/assets/synara/azeite.webp"
                        title={<>Mar<em className="italic font-normal text-emerald-800">Fit</em> Meals</>}
                        tagline="Marmitas fitness, prontas a comer"
                        description="Comida real, equilibrada, entregue à tua porta. Escolhe o teu pack semanal e liberta-te da rotina do preparar-limpar-repetir."
                        cta="Ver planos e preços"
                        testid="splash-card-marfit"
                    />
                </div>

                <footer className="mt-14 text-center text-xs text-stone-500">
                    <a
                        href="https://wa.me/351924949524"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-stone-800 transition-colors"
                    >
                        WhatsApp · +351 924 949 524
                    </a>
                    <span className="mx-2 text-stone-400">·</span>
                    <a
                        href="mailto:synarapersonalchef@gmail.com"
                        className="hover:text-stone-800 transition-colors"
                    >
                        synarapersonalchef@gmail.com
                    </a>
                </footer>
            </div>
        </div>
    );
};

const ProductCard = ({ to, variant, Icon, eyebrow, image, title, tagline, description, cta, testid }) => {
    const chef = variant === "chef";
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: chef ? 0.25 : 0.4 }}
            whileHover={{ y: -6 }}
            className="h-full"
        >
            <Link
                to={to}
                data-testid={testid}
                className={`group block h-full rounded-3xl overflow-hidden ring-1 transition-shadow duration-300 hover:shadow-2xl ${
                    chef
                        ? "bg-white ring-rose-200/60 hover:shadow-rose-200/40"
                        : "bg-[#faf5ea] ring-emerald-800/20 hover:shadow-emerald-900/20"
                }`}
            >
                {/* Visual header band with real photo */}
                <div className="h-52 md:h-64 relative overflow-hidden">
                    {image ? (
                        <img
                            src={image}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            style={{ objectPosition: chef ? "center 25%" : "center" }}
                            loading="eager"
                        />
                    ) : (
                        <div
                            className={`absolute inset-0 ${
                                chef
                                    ? "bg-gradient-to-br from-rose-50 to-rose-100"
                                    : "bg-gradient-to-br from-[#c67b3f] via-[#b26a35] to-[#7a9058]"
                            }`}
                        />
                    )}
                    {/* Colored tint overlay for brand consistency + label legibility */}
                    <div
                        aria-hidden
                        className={`absolute inset-0 ${
                            chef
                                ? "bg-gradient-to-t from-rose-950/45 via-rose-900/10 to-transparent"
                                : "bg-gradient-to-t from-[#3d2e1f]/55 via-[#3d2e1f]/10 to-transparent"
                        }`}
                    />
                    {/* Eyebrow badge */}
                    <span
                        className={`absolute top-4 left-4 z-10 text-[10px] uppercase tracking-[0.24em] font-medium px-2.5 py-1 rounded-full backdrop-blur-sm ${
                            chef
                                ? "bg-white/85 text-rose-800"
                                : "bg-[#faf5ea]/90 text-[#a4632f]"
                        }`}
                    >
                        {eyebrow}
                    </span>
                    {/* Small icon chip bottom-right for identity */}
                    <span
                        className={`absolute bottom-4 right-4 z-10 h-11 w-11 rounded-full flex items-center justify-center backdrop-blur-sm ring-1 ${
                            chef
                                ? "bg-white/85 text-rose-700 ring-white/60"
                                : "bg-[#faf5ea]/90 text-[#a4632f] ring-[#faf5ea]/70"
                        }`}
                    >
                        <Icon size={20} strokeWidth={1.4} />
                    </span>
                </div>

                <div className="p-6 md:p-8 flex flex-col h-[calc(100%-13rem)] md:h-[calc(100%-16rem)]">
                    <h2 className={`font-serif text-3xl md:text-4xl leading-tight ${
                        chef ? "text-stone-900" : "text-[#3d2e1f]"
                    }`}>
                        {title}
                    </h2>
                    <p className={`mt-2 text-sm font-medium tracking-wide ${
                        chef ? "text-rose-800" : "text-[#7a9058]"
                    }`}>
                        {tagline}
                    </p>
                    <p className="mt-4 text-stone-600 leading-relaxed text-sm md:text-base flex-1">
                        {description}
                    </p>
                    <div
                        className={`mt-6 inline-flex items-center gap-2 font-medium text-sm group-hover:gap-3 transition-all ${
                            chef ? "text-rose-800" : "text-[#c67b3f]"
                        }`}
                    >
                        {cta}
                        <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </div>
                </div>
            </Link>
        </motion.div>
    );
};

export default Splash;
